import React, { useState, useEffect, useRef } from 'react';
import { collection, getDocs, query, where, orderBy, limit, startAfter, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { auth } from '../firebase';
import { EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useRole } from '../hooks/useRole';
import { pickBadge, hasBackOrder, backOrderCount, collectableCount } from '../utils/pickStatus';
import { useTheme } from '../context/ThemeContext';
import { useData } from '../context/DataContext';
import Sidebar, { SIDEBAR_W } from '../components/Sidebar';
import { compareNames, nameMatchRank } from '../utils/nameSort';
import {
  isLooseQuery, looseIncludes, nameMatches, canonicalDocNumbers, documentNumberMatches,
} from '../utils/searchMatch';
import { resolveInventoryStatus } from '../utils/inventoryLedger';
import { getPaymentStatus, PAYMENT_STATUS, fetchPaymentsForInvoices } from '../utils/paymentStatus';

// Normalize a document-number search term into prefix candidates for a Firestore
// range query. Always tries the raw term as a prefix; if the term is all digits
// (e.g. "6055"), also tries current-year + zero-padded to 6 (e.g. "26-006055").
function buildDocNumberCandidates(term) {
  const raw = (term || '').trim();
  if (!raw) return [];
  const candidates = new Set();
  candidates.add(raw); // prefix: covers "26-006055", "26-0060", "26-"
  if (/^\d+$/.test(raw)) {
    const yy = new Date().getFullYear().toString().slice(-2);
    candidates.add(`${yy}-${raw.padStart(6, '0')}`);
  }
  // FORGIVING FORMS, from the shared canonicaliser: "26 6389", "266389" and
  // last year's numbers all rebuild into the exact stored string. Added to
  // the prefix candidates above, never instead of them, so every query that
  // found a document before still does.
  if (isLooseQuery(raw)) canonicalDocNumbers(raw).forEach(c => candidates.add(c));
  return [...candidates];
}

// ── HOW ONE FIELD MATCHES ONE QUERY ──────────────────────────────────
//
// Every search box on this page, and the carried-in Dashboard search, decide
// "does this document match" through these three — the client-side half of
// searching. The server queries below decide what is FETCHED; these decide
// what is SHOWN, and a document has to pass both.
//
// Before, all three were a plain lowercase substring test. That half was
// already case-insensitive and matched anywhere; the strictness lived in the
// SERVER half, where document number and P.O. were Firestore range queries —
// case-sensitive, and matching only from the first character. "PO-4471-A"
// was only reachable by typing its exact start, "PO-4471", in capitals.
//
// Now, for a query of 3 or more letters and digits:
//   document number  punctuation-free substring, or a canonical rebuild of
//                    the number ("26 6389" and "266389" -> 26-006389)
//   customer name    nameMatches: punctuation-insensitive, spaces-insensitive
//                    only from the start of a word (see searchMatch.js)
//   P.O. number      punctuation-free substring anywhere
//
// Under 3, exactly the old test. Loosened, a one- or two-character query is a
// substring of a large share of every number on file.
//
// Callers pass a TRIMMED, non-empty query.
const strictIncludes = (value, q) => String(value ?? '').toLowerCase().includes(q.toLowerCase());
const docNumberHit = (d, q) => (isLooseQuery(q) ? documentNumberMatches(d.docNumber, q) : strictIncludes(d.docNumber, q));
const customerNameHit = (d, q) => (isLooseQuery(q) ? nameMatches(d.customerName, q) : strictIncludes(d.customerName, q));
const poNumberHit = (d, q) => (isLooseQuery(q) ? looseIncludes(d.poNumber, q) : strictIncludes(d.poNumber, q));
const anyFieldHit = (d, q) => docNumberHit(d, q) || customerNameHit(d, q) || poNumberHit(d, q);

function Documents({ user }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAdmin, isSalesperson, userName: roleName, loading: roleLoading } = useRole();
  const { theme, themeName } = useTheme();
  const { customers } = useData();
  const isDark = themeName === 'dark';

  // Flat blue palette — overrides the dark theme vars. No logic depends on theme.
  // Semantic greens/ambers/reds keep their meaning; the `${C_X}26`/`4D` alpha
  // appends downstream become valid 8-digit-hex tints of these blues/semantics.
  const C_TEXT = '#03045E';
  const C_TEXT_2 = '#6B7C93';
  const C_TEXT_3 = '#6B7C93';
  const C_BORDER = '#E3ECF5';
  const C_BG_CARD = '#FFFFFF';
  const C_BG_CARD_SOLID = '#FFFFFF';
  const C_BG_NESTED = '#CDE7F5';
  const C_CYAN = '#0077B6';
  const C_CYAN_BRIGHT = '#0077B6';
  const C_VIOLET = '#0096C7';
  const C_VIOLET_BRIGHT = '#0096C7';
  const C_GREEN = '#0F6E56';
  const C_GREEN_BRIGHT = '#0F6E56';
  const C_ORANGE = '#854F0B';
  const C_ORANGE_BRIGHT = '#854F0B';
  const C_RED = '#A32D2D';
  const C_RED_BRIGHT = '#A32D2D';
  const GRADIENT = '#0077B6';

  const cardStyle = {
    background: C_BG_CARD,
    backdropFilter: 'none',
    WebkitBackdropFilter: 'none',
    border: `1px solid ${C_BORDER}`,
    borderRadius: 16,
    padding: 20,
    marginBottom: 16,
  };
  const inputStyle = {
    width: '100%',
    padding: '10px 14px',
    background: '#FFFFFF',
    border: `1px solid ${C_BORDER}`,
    borderRadius: 10,
    color: C_TEXT,
    fontSize: 14,
    outline: 'none',
    transition: 'border-color 0.3s, box-shadow 0.3s, background 0.3s',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  };
  const labelStyle = {
    color: C_TEXT_2,
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 1,
    textTransform: 'uppercase',
    marginBottom: 6,
    display: 'block',
  };
  const gradientButton = {
    background: GRADIENT, border: 'none', color: '#fff',
    padding: '12px 22px', borderRadius: 12,
    fontSize: 14, fontWeight: 700, cursor: 'pointer',
    letterSpacing: 0.5, boxShadow: `0 4px 20px ${C_CYAN}4D`,
    transition: 'transform 0.3s, box-shadow 0.3s',
  };
  const cyanGlassButton = {
    background: `${C_CYAN}1A`, border: `1px solid ${C_CYAN}4D`,
    color: C_CYAN_BRIGHT, padding: '10px 18px', borderRadius: 10,
    fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'background 0.3s',
  };
  const violetGlassButton = {
    background: `${C_VIOLET}1A`, border: `1px solid ${C_VIOLET}4D`,
    color: C_VIOLET_BRIGHT, padding: '10px 18px', borderRadius: 10,
    fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'background 0.3s',
  };
  const greenGlassButton = {
    background: `${C_GREEN}1A`, border: `1px solid ${C_GREEN}4D`,
    color: C_GREEN_BRIGHT, padding: '12px 22px', borderRadius: 12,
    fontSize: 14, fontWeight: 700, cursor: 'pointer', transition: 'background 0.3s',
    letterSpacing: 0.5,
  };
  const redGlassButton = {
    background: `${C_RED}1A`, border: `1px solid ${C_RED}4D`,
    color: C_RED_BRIGHT, padding: '6px 12px', borderRadius: 8,
    fontSize: 11, fontWeight: 600, cursor: 'pointer', transition: 'background 0.3s',
  };
  const neutralBg = '#CDE7F5';
  const neutralHover = '#E6F1FB';
  const darkButton = {
    background: neutralBg, border: `1px solid ${C_BORDER}`,
    color: C_TEXT, padding: '10px 18px', borderRadius: 10,
    fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'background 0.3s',
  };
  const focusInputCyan = (e) => {
    e.target.style.borderColor = `${C_CYAN}80`;
    e.target.style.boxShadow = `0 0 0 3px ${C_CYAN}1A`;
  };
  const blurInputDefault = (e) => {
    e.target.style.borderColor = C_BORDER;
    e.target.style.boxShadow = 'none';
  };

  const PAGE_SIZE = 50;

  const [quotes, setQuotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [lastQuoteDoc, setLastQuoteDoc] = useState(null);
  const [lastInvoiceDoc, setLastInvoiceDoc] = useState(null);
  const [hasMoreQuotes, setHasMoreQuotes] = useState(false);
  const [hasMoreInvoices, setHasMoreInvoices] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') === 'invoices' ? 'invoices' : 'quotes');
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [docToDelete, setDocToDelete] = useState(null);
  const [searchDocNumber, setSearchDocNumber] = useState('');
  const [searchCustomerName, setSearchCustomerName] = useState('');
  const [searchPoNumber, setSearchPoNumber] = useState('');
  // Combined quick-search carried in from the Dashboard search bar (?search=…).
  // Unlike the three field filters above (which AND together), this ORs across
  // doc number, customer name, and P.O. so a single term lands pre-filtered.
  const [quickSearch, setQuickSearch] = useState(() => searchParams.get('search') || '');
  const didAutoTab = useRef(false);
  // Server-side search results (both collections, tagged with __col) + status.
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const searchSeqRef = useRef(0); // guards against stale out-of-order query results
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [salespersonFilter, setSalespersonFilter] = useState('All');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('All');
  const [inventoryFilter, setInventoryFilter] = useState('all'); // 'all' | 'pending' | 'deducted' (invoices only)

  // ── PAYMENT RECORDS FOR THE INVOICES ON SCREEN ──
  // The list's payment badge and payment filter are computed from these, never
  // from the isPaid / isPartialPaid / totalPaid stored on the invoice (see
  // utils/paymentStatus.js for why those went stale).
  //   payRecords[invoiceId]  that invoice's payment records
  //   payLoad[invoiceId]     'loading' | 'ok' | 'error' (absent = not asked for yet)
  const [payRecords, setPayRecords] = useState({});
  const [payLoad, setPayLoad] = useState({});
  // Bumped to make the read effect look again: after a reload, or on Retry.
  const [payTick, setPayTick] = useState(0);
  // Ids already asked for, so a re-render never asks twice.
  const payRequestedRef = useRef(new Set());
  // Generation of the cache. A read that started before a reload must not
  // write its (older) answer into the new cache.
  const payGenRef = useRef(0);

  useEffect(() => {
    loadDocuments();
  }, []);

  // When arriving with a carried-in search, land on whichever tab has matches.
  // Default is Quotes; switch to Invoices once (only) if the term matches an
  // invoice but no quote, so the user sees the match without re-clicking tabs.
  useEffect(() => {
    if (didAutoTab.current || loading || roleLoading) return;
    const t = (quickSearch || '').trim().toLowerCase();
    if (!t) { didAutoTab.current = true; return; }
    // The same forgiving rule as the list itself, so "4471" lands on the tab
    // that holds PO-4471-A rather than on an empty one.
    const matches = (arr) => arr.some(d =>
      (isAdmin || d.salesperson === roleName) && anyFieldHit(d, quickSearch.trim())
    );
    if (!matches(quotes) && matches(invoices)) setActiveTab('invoices');
    didAutoTab.current = true;
  }, [loading, roleLoading, quotes, invoices, quickSearch, isAdmin, roleName]);

  // Switch tabs AND reflect the choice in the URL (?tab=), so the sidebar's
  // Quotes/Invoices highlight stays in sync and the tab is deep-linkable.
  // Other params (e.g. ?search=) are preserved.
  const switchTab = (tab) => {
    setActiveTab(tab);
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };

  // Sync the active tab when the ?tab= param changes (e.g. clicking the other
  // sidebar tab while already on /documents — the component doesn't remount, so
  // the useState initializer alone won't catch it).
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'invoices' && activeTab !== 'invoices') setActiveTab('invoices');
    else if (tabParam === 'quotes' && activeTab !== 'quotes') setActiveTab('quotes');
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadDocuments = async () => {
    setLoading(true);
    // Payment records are dropped with the list they were read for, so a
    // reload (after a delete, say) re-reads them rather than showing statuses
    // from before it.
    payGenRef.current += 1;
    payRequestedRef.current = new Set();
    setPayRecords({});
    setPayLoad({});
    setPayTick(t => t + 1);
    try {
      poIndexRef.current = null; // a reload means the P.O. index may be stale
      const qSnap = await getDocs(query(collection(db, 'quotes'), orderBy('createdAt', 'desc'), limit(PAGE_SIZE)));
      setQuotes(qSnap.docs.map(d => ({ ...d.data(), id: d.id })));
      setLastQuoteDoc(qSnap.docs[qSnap.docs.length - 1] || null);
      setHasMoreQuotes(qSnap.docs.length === PAGE_SIZE);

      const iSnap = await getDocs(query(collection(db, 'invoices'), orderBy('createdAt', 'desc'), limit(PAGE_SIZE)));
      setInvoices(iSnap.docs.map(d => ({ ...d.data(), id: d.id })));
      setLastInvoiceDoc(iSnap.docs[iSnap.docs.length - 1] || null);
      setHasMoreInvoices(iSnap.docs.length === PAGE_SIZE);
    } catch (err) {
      console.error('Error loading documents:', err);
    }
    setLoading(false);
  };

  const loadMoreDocuments = async () => {
    const isQuotes = activeTab === 'quotes';
    const lastDoc = isQuotes ? lastQuoteDoc : lastInvoiceDoc;
    if (!lastDoc) return;
    setLoadingMore(true);
    try {
      const col = isQuotes ? 'quotes' : 'invoices';
      const snap = await getDocs(
        query(collection(db, col), orderBy('createdAt', 'desc'), startAfter(lastDoc), limit(PAGE_SIZE))
      );
      const newDocs = snap.docs.map(d => ({ ...d.data(), id: d.id }));
      if (isQuotes) {
        setQuotes(prev => [...prev, ...newDocs]);
        setLastQuoteDoc(snap.docs[snap.docs.length - 1] || null);
        setHasMoreQuotes(snap.docs.length === PAGE_SIZE);
      } else {
        setInvoices(prev => [...prev, ...newDocs]);
        setLastInvoiceDoc(snap.docs[snap.docs.length - 1] || null);
        setHasMoreInvoices(snap.docs.length === PAGE_SIZE);
      }
    } catch (err) {
      console.error('Error loading more documents:', err);
    }
    setLoadingMore(false);
  };

  // ── Server-side search (finds documents beyond the loaded newest-50 slice) ──
  // Doc number → complete + prefix range; customer name → resolve to id(s) via the
  // cached customers list then query by customerId; P.O. → prefix range. Every
  // query uses a SINGLE field (range OR equality) with no cross-field orderBy, so
  // none requires a composite index.
  const dedupeById = (arr) => {
    const seen = new Set();
    const out = [];
    arr.forEach(r => { if (!seen.has(r.id)) { seen.add(r.id); out.push(r); } });
    return out;
  };

  // Run a query across BOTH collections, tagging each hit with its source so the
  // display can respect the active tab.
  // ── EVERY DOCUMENT THAT CARRIES A P.O. ──
  //
  // Firestore cannot match "4471" inside "PO-4471-A": it has no substring or
  // case-insensitive operator, and P.O. numbers follow no format that could be
  // rebuilt into the exact string the way document numbers can. So a forgiving
  // P.O. search reads every quote and invoice that HAS a P.O. and compares
  // them here.
  //
  // where('poNumber', '>', '') returns only documents with a non-empty P.O. —
  // a single-field inequality, so no composite index — and the result is kept
  // for the rest of the visit: the first forgiving P.O. search pays for the
  // read, every keystroke after it filters in memory. It is dropped whenever
  // the document list reloads (see loadDocuments), so an edit or a delete is
  // never searched stale.
  const poIndexRef = useRef(null);
  const loadPoIndex = () => {
    if (!poIndexRef.current) {
      poIndexRef.current = queryBothCollections(col => query(
        collection(db, col),
        where('poNumber', '>', ''),
      )).catch(err => { poIndexRef.current = null; throw err; });
    }
    return poIndexRef.current;
  };

  const queryBothCollections = async (buildQuery) => {
    const out = [];
    for (const col of ['quotes', 'invoices']) {
      const snap = await getDocs(buildQuery(col));
      snap.docs.forEach(d => out.push({ ...d.data(), id: d.id, __col: col }));
    }
    return out;
  };

  const searchByDocNumber = async (term) => {
    const candidates = buildDocNumberCandidates(term);
    const all = [];
    for (const cand of candidates) {
      const hits = await queryBothCollections(col => query(
        collection(db, col),
        where('docNumber', '>=', cand),
        where('docNumber', '<=', cand + ''),
        limit(25),
      ));
      all.push(...hits);
    }
    return dedupeById(all);
  };

  const searchByPoNumber = async (term) => {
    const t = (term || '').trim();
    if (!t) return [];
    if (!isLooseQuery(t)) {
      // Under the forgiving length: exactly as before, a prefix range.
      return dedupeById(await queryBothCollections(col => query(
        collection(db, col),
        where('poNumber', '>=', t),
        where('poNumber', '<=', t + '\uf8ff'),
        limit(25),
      )));
    }
    const withPo = await loadPoIndex();
    return dedupeById(withPo.filter(d => looseIncludes(d.poNumber, t)));
  };

  const searchByCustomerName = async (term) => {
    const raw = (term || '').trim();
    if (!raw) return [];
    const t = raw.toLowerCase();
    const loose = isLooseQuery(raw);
    const matchedIds = customers
      .filter(c => (loose ? nameMatches(c.name, raw) : c.name?.toLowerCase().includes(t)))
      // BEST MATCHES FIRST, THEN CAP. The cap below is unchanged, but it used to
      // keep whichever ten customers happened to load first; a forgiving match
      // finds more names, and "smith" must not drop Smith Fencing because
      // eleven other names contain the word. Ranked with the Customers page's
      // own rules, ties alphabetical.
      .map(c => ({ c, r: nameMatchRank(c.name, raw) }))
      .sort((a, b) => ((a.r ?? 3) - (b.r ?? 3)) || compareNames(a.c.name, b.c.name))
      .slice(0, 10) // bound the number of customerId queries
      .map(x => x.c.id);
    if (matchedIds.length === 0) return [];
    const all = [];
    for (const custId of matchedIds) {
      const hits = await queryBothCollections(col => query(
        collection(db, col),
        where('customerId', '==', custId),
        limit(25),
      ));
      all.push(...hits);
    }
    return dedupeById(all);
  };

  const runServerSearch = async () => {
    const seq = ++searchSeqRef.current; // ignore results from superseded runs
    setSearching(true);
    try {
      const resultSets = [];
      // Dashboard quickSearch = OR across all three fields (matches today's semantics).
      if (quickSearch.trim()) {
        const orHits = [
          ...(await searchByDocNumber(quickSearch)),
          ...(await searchByCustomerName(quickSearch)),
          ...(await searchByPoNumber(quickSearch)),
        ];
        resultSets.push(dedupeById(orHits));
      }
      // Explicit field boxes = AND (intersected below).
      // Blank or whitespace-only boxes are skipped: pushing an empty result set
      // for one would intersect every other box's results down to nothing.
      if (searchDocNumber.trim()) resultSets.push(await searchByDocNumber(searchDocNumber));
      if (searchCustomerName.trim()) resultSets.push(await searchByCustomerName(searchCustomerName));
      if (searchPoNumber.trim()) resultSets.push(await searchByPoNumber(searchPoNumber));

      let merged;
      if (resultSets.length === 0) {
        merged = [];
      } else if (resultSets.length === 1) {
        merged = resultSets[0];
      } else {
        // Intersect by id across all active sets (AND across the boxes).
        const idSets = resultSets.map(s => new Set(s.map(r => r.id)));
        merged = resultSets[0].filter(r => idSets.every(s => s.has(r.id)));
      }
      if (seq === searchSeqRef.current) setSearchResults(dedupeById(merged));
    } catch (err) {
      console.error('Server search error:', err);
      // A missing composite index surfaces here with a console link in the message
      // (the queries above are designed to avoid needing one).
      if (err?.message) console.error('Search query detail:', err.message);
      if (seq === searchSeqRef.current) setSearchResults([]);
    } finally {
      if (seq === searchSeqRef.current) setSearching(false);
    }
  };

  // Debounce server search on the three field terms + the Dashboard quickSearch.
  // Runs on mount too, so a ?search= term carried in from the Dashboard triggers a
  // server query immediately (the primary "old docs not found" entry point).
  useEffect(() => {
    // Whitespace alone is not a search.
    const anyTerm = [quickSearch, searchDocNumber, searchCustomerName, searchPoNumber].some(v => (v || '').trim());
    if (!anyTerm) {
      setSearchResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    const t = setTimeout(() => { runServerSearch(); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quickSearch, searchDocNumber, searchCustomerName, searchPoNumber]);

  const initiateDelete = (document) => {
    if (!isAdmin) { alert('Only admins can delete documents.'); return; }
    setDocToDelete(document);
    setDeletePassword('');
    setDeleteError('');
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!docToDelete) return;
    setDeleting(docToDelete.id);
    setDeleteError('');
    try {
      const credential = EmailAuthProvider.credential(auth.currentUser.email, deletePassword);
      await reauthenticateWithCredential(auth.currentUser, credential);

      const collectionName = docToDelete.docType === 'quote' ? 'quotes' : 'invoices';
      await deleteDoc(doc(db, collectionName, docToDelete.id));

      setShowDeleteModal(false);
      setDocToDelete(null);
      setDeletePassword('');
      loadDocuments();
    } catch (err) {
      console.error('Delete error:', err);
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setDeleteError('❌ Incorrect password. Please try again.');
      } else {
        setDeleteError('❌ Error: ' + err.message);
      }
    }
    setDeleting(null);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US');
  };

  // Any active search term switches the list from the browse slice to server results.
  // Any NON-BLANK search term switches the list to server results; a box
  // holding only spaces leaves the ordinary date-ordered list alone.
  const searchMode = [quickSearch, searchDocNumber, searchCustomerName, searchPoNumber].some(v => (v || '').trim());
  const activeCol = activeTab === 'quotes' ? 'quotes' : 'invoices';
  const rawDocuments = searchMode
    ? searchResults.filter(d => d.__col === activeCol)
    : (activeTab === 'quotes' ? quotes : invoices);

  const roleFilteredDocuments = roleLoading
    ? []
    : isAdmin
      ? rawDocuments
      : rawDocuments.filter(d => d.salesperson === roleName);

  // ── THE PAYMENTS READ ─────────────────────────────────────────────────
  //
  // This list does not load payment records with the invoices, so it reads
  // them here, for the invoices this user can see on the Invoices tab —
  // browse page or search results — and only for ids not already read.
  //
  // BATCHED, never one read per invoice: the same fetchPaymentsForInvoices the
  // customer profile uses, which asks for up to 30 invoice ids per query (the
  // most a Firestore `in` filter accepts). A fresh page of 50 is therefore TWO
  // queries, sent together; Load More adds two more for its 50. Nothing is read
  // on the Quotes tab, which has no payment badge.
  //
  // The list itself does not wait for this. Rows render as soon as the
  // invoices arrive; their payment chip reads CHECKING… until the records do.
  const payIdsKey = activeTab === 'invoices' ? roleFilteredDocuments.map(d => d.id).join('|') : '';
  useEffect(() => {
    if (!payIdsKey) return;
    const wanted = payIdsKey.split('|').filter(id => !payRequestedRef.current.has(id));
    if (wanted.length === 0) return;
    wanted.forEach(id => payRequestedRef.current.add(id));
    const gen = payGenRef.current;
    const mark = (state) => setPayLoad(prev => {
      const next = { ...prev };
      wanted.forEach(id => { next[id] = state; });
      return next;
    });
    mark('loading');
    fetchPaymentsForInvoices(wanted)
      .then(byInvoice => {
        if (gen !== payGenRef.current) return;
        setPayRecords(prev => ({ ...prev, ...byInvoice }));
        mark('ok');
      })
      .catch(err => {
        if (gen !== payGenRef.current) return;
        // NOT a fallback to the stored flags. A stale PAID is the bug this
        // replaced; these rows say STATUS UNAVAILABLE until a read succeeds.
        console.error('Error loading payment records for the documents list:', err);
        mark('error');
      });
  }, [payIdsKey, payTick]);

  // Ask again for every invoice whose read failed.
  const retryPayments = () => {
    const failed = Object.keys(payLoad).filter(id => payLoad[id] === 'error');
    failed.forEach(id => payRequestedRef.current.delete(id));
    setPayLoad(prev => {
      const next = { ...prev };
      failed.forEach(id => { delete next[id]; });
      return next;
    });
    setPayTick(t => t + 1);
  };

  // One invoice's payment status on this list, as { state, result }:
  //   'ok'       result = getPaymentStatus(grandTotal, its payment records) —
  //              the same function, and so the same answer, as the invoice
  //              screen and the customer profile
  //   'loading'  records not read yet; result null
  //   'error'    the read failed; result null — shown as STATUS UNAVAILABLE
  const listPaymentStatus = (d) => {
    const state = payLoad[d.id] || 'loading';
    return state === 'ok'
      ? { state, result: getPaymentStatus(d.grandTotal, payRecords[d.id] || []) }
      : { state, result: null };
  };

  const salespersonNames = isAdmin
    ? ['All', ...Array.from(new Set(rawDocuments.map(d => d.salesperson).filter(Boolean))).sort()]
    : [];

  // Clear the carried-in quick search and drop it from the URL so a refresh
  // won't re-apply it.
  const clearQuickSearch = () => {
    setQuickSearch('');
    setSearchParams({}, { replace: true });
  };

  const clearFilters = () => {
    setSearchDocNumber('');
    setSearchCustomerName('');
    setSearchPoNumber('');
    setStartDate('');
    setEndDate('');
    setSalespersonFilter('All');
    setPaymentStatusFilter('All');
    setInventoryFilter('all');
    clearQuickSearch();
  };

  const passesFilters = (d, { ignorePayment = false } = {}) => {
    // Same three fields as before, each through its forgiving predicate. The
    // Dashboard search is still an OR across all three; the boxes still AND.
    const qQuick = quickSearch.trim();
    if (qQuick && !anyFieldHit(d, qQuick)) return false;
    const qDoc = searchDocNumber.trim();
    if (qDoc && !docNumberHit(d, qDoc)) return false;
    const qName = searchCustomerName.trim();
    if (qName && !customerNameHit(d, qName)) return false;
    const qPo = searchPoNumber.trim();
    if (qPo && !poNumberHit(d, qPo)) return false;
    if (isAdmin && salespersonFilter !== 'All' && d.salesperson !== salespersonFilter) return false;
    if (!ignorePayment && activeTab === 'invoices' && paymentStatusFilter !== 'All') {
      // The COMPUTED status — the one the badge beside it shows — so the filter
      // and the badges cannot disagree. Each option matches exactly one badge.
      // An invoice whose status is not known (still loading, or its read
      // failed) matches no payment option: it cannot be called Paid or Unpaid
      // without guessing. How many were left out is shown beside the count.
      const { result } = listPaymentStatus(d);
      if (!result || result.status !== paymentStatusFilter) return false;
    }
    if (activeTab === 'invoices' && inventoryFilter !== 'all') {
      if (resolveInventoryStatus(d) !== inventoryFilter) return false;
    }
    if (startDate || endDate) {
      const docDate = new Date(d.createdAt);
      docDate.setHours(0, 0, 0, 0);
      if (startDate) {
        const from = new Date(startDate + 'T00:00:00');
        if (docDate < from) return false;
      }
      if (endDate) {
        const to = new Date(endDate + 'T00:00:00');
        if (docDate > to) return false;
      }
    }
    return true;
  };
  const filteredDocuments = roleFilteredDocuments.filter(d => passesFilters(d));

  // Invoices that pass every OTHER filter but were left out by a payment filter
  // only because their status is not known yet, or could not be read.
  const paymentFilterActive = activeTab === 'invoices' && paymentStatusFilter !== 'All';
  const unknownLeftOut = paymentFilterActive
    ? roleFilteredDocuments.filter(d => !listPaymentStatus(d).result && passesFilters(d, { ignorePayment: true }))
    : [];
  const unknownLeftOutFailed = unknownLeftOut.filter(d => payLoad[d.id] === 'error').length;
  const payErrorCount = activeTab === 'invoices'
    ? roleFilteredDocuments.filter(d => payLoad[d.id] === 'error').length
    : 0;

  // ── ORDER ─────────────────────────────────────────────────────────────
  // filteredDocuments decides WHICH documents are shown; this only orders them.
  //
  // NO SEARCH: left exactly as it arrives — the browse query's
  // orderBy('createdAt', 'desc'), newest first. That is right for a working
  // list and is deliberately untouched. Date, salesperson, payment and
  // inventory filters on their own do not count as a search and keep it too.
  //
  // WITH A SEARCH: grouped by customer, A to Z, using the SAME comparator as
  // the Customers page, so a name sorts identically on both.
  //
  // Within one customer, newest first. Note this is NOT "the order they
  // already had": search results arrive in merge order — document-number hits,
  // then customer-name hits, then P.O. hits — which has no date order at all.
  // Sorting each customer's documents by date explicitly is what gives them
  // the newest-first order the no-search list has.
  const newestFirst = (a, b) => (Date.parse(b.createdAt) || 0) - (Date.parse(a.createdAt) || 0);
  const documents = searchMode
    ? [...filteredDocuments].sort((a, b) =>
        compareNames(a.customerName, b.customerName) || newestFirst(a, b))
    : filteredDocuments;

  const hasFilters = quickSearch || searchDocNumber || searchCustomerName || searchPoNumber || startDate || endDate || salespersonFilter !== 'All' || paymentStatusFilter !== 'All' || inventoryFilter !== 'all';

  const displayName = roleName || user?.email?.split('@')[0]?.toUpperCase();

  const quoteCount = roleLoading ? '…' : (isAdmin ? quotes : quotes.filter(d => d.salesperson === roleName)).length;
  const invoiceCount = roleLoading ? '…' : (isAdmin ? invoices : invoices.filter(d => d.salesperson === roleName)).length;
  // No "Load More" in search mode — server results aren't the paginated browse slice.
  const hasMore = !searchMode && (activeTab === 'quotes' ? hasMoreQuotes : hasMoreInvoices);

  const isQuotesTab = activeTab === 'quotes';
  const accentColor = isQuotesTab ? C_CYAN_BRIGHT : C_VIOLET_BRIGHT;
  const accentBg = isQuotesTab ? `${C_CYAN}1A` : `${C_VIOLET}1A`;
  const accentBorder = isQuotesTab ? `${C_CYAN}4D` : `${C_VIOLET}4D`;
  const accentBorderHover = isQuotesTab ? `${C_CYAN}4D` : `${C_VIOLET}4D`;
  const accentShadow = isQuotesTab ? `0 8px 30px ${C_CYAN}1A` : `0 8px 30px ${C_VIOLET}1A`;

  return (
    <>
      <Sidebar />
      <div style={{
        minHeight: '100vh',
        marginLeft: SIDEBAR_W,
        background: '#CDE7F5',
        padding: 24,
        color: C_TEXT,
        transition: 'background 0.3s ease, color 0.3s ease',
      }}>
        <style>{`
          select.dark-select option { background: #FFFFFF; color: #03045E; }
          input[type="date"].dark-date::-webkit-calendar-picker-indicator { filter: invert(0); }
        `}</style>

        <div style={{ maxWidth: 1280, margin: '0 auto' }}>

          {/* Header bar */}
          <div style={{ ...cardStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, minWidth: 0 }}>
              <div>
                <h1 style={{
                  fontSize: 22, fontWeight: 800, margin: 0,
                  color: C_TEXT,
                }}>
                  Documents
                </h1>
                <p style={{ color: C_TEXT_2, fontSize: 12, margin: '2px 0 0 0', letterSpacing: 1, textTransform: 'uppercase', fontWeight: 600 }}>
                  Quotes & Invoices
                </p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ color: C_TEXT_2, fontSize: 13 }}>
                {isAdmin ? 'Admin' : 'Salesperson'}: <span style={{ color: C_VIOLET_BRIGHT, fontWeight: 700 }}>{displayName}</span>
              </span>
              {!roleLoading && (
                <span style={{
                  background: isAdmin ? `${C_VIOLET}26` : neutralBg,
                  color: isAdmin ? C_VIOLET_BRIGHT : C_TEXT_2,
                  border: isAdmin ? `1px solid ${C_VIOLET}4D` : `1px solid ${C_BORDER}`,
                  padding: '3px 10px', borderRadius: 6,
                  fontSize: 11, fontWeight: 700, letterSpacing: 0.5,
                }}>
                  {isAdmin ? 'ADMIN' : 'SALESPERSON'}
                </span>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div style={{
            background: C_BG_CARD,
            backdropFilter: 'none',
            border: `1px solid ${C_BORDER}`,
            borderRadius: 16, padding: 8, marginBottom: 16,
            display: 'flex', gap: 8, flexWrap: 'wrap',
          }}>
            <button
              onClick={() => switchTab('quotes')}
              style={{
                flex: 1, minWidth: 160,
                padding: 12,
                background: activeTab === 'quotes' ? `${C_CYAN}26` : 'transparent',
                border: activeTab === 'quotes' ? `1px solid ${C_CYAN}80` : '1px solid transparent',
                borderRadius: 12,
                color: activeTab === 'quotes' ? C_CYAN_BRIGHT : C_TEXT_3,
                fontSize: 14, fontWeight: 700, cursor: 'pointer',
                transition: 'background 0.3s, border-color 0.3s, color 0.3s',
              }}>
              📄 Quotes ({quoteCount})
            </button>
            <button
              onClick={() => switchTab('invoices')}
              style={{
                flex: 1, minWidth: 160,
                padding: 12,
                background: activeTab === 'invoices' ? `${C_VIOLET}26` : 'transparent',
                border: activeTab === 'invoices' ? `1px solid ${C_VIOLET}80` : '1px solid transparent',
                borderRadius: 12,
                color: activeTab === 'invoices' ? C_VIOLET_BRIGHT : C_TEXT_3,
                fontSize: 14, fontWeight: 700, cursor: 'pointer',
                transition: 'background 0.3s, border-color 0.3s, color 0.3s',
              }}>
              🧾 Invoices ({invoiceCount})
            </button>
            <div style={{ flex: '0 0 auto', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <button onClick={() => navigate('/new-quote')} style={gradientButton}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = `0 8px 30px ${C_CYAN}80`;
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = `0 4px 20px ${C_CYAN}4D`;
                }}>
                + New Quote
              </button>
              <button onClick={() => navigate('/new-invoice')} style={greenGlassButton}
                onMouseEnter={e => e.currentTarget.style.background = `${C_GREEN}2E`}
                onMouseLeave={e => e.currentTarget.style.background = `${C_GREEN}1A`}>
                + New Invoice
              </button>
            </div>
          </div>

          {/* Filters card */}
          <div style={cardStyle}>
            {/* Active quick-search carried in from the Dashboard — shows what's
                filtering and lets the user clear it. */}
            {quickSearch && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <span style={{ background: `${C_CYAN}26`, border: `1px solid ${C_CYAN}80`, color: C_CYAN_BRIGHT, padding: '6px 12px', borderRadius: 999, fontSize: 13, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  🔍 Searching “{quickSearch}” — number, customer, or P.O.
                  <button onClick={clearQuickSearch} title="Clear search"
                    style={{ background: 'none', border: 'none', color: C_CYAN_BRIGHT, cursor: 'pointer', fontWeight: 700, fontSize: 14, lineHeight: 1, padding: 0 }}>✕</button>
                </span>
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10, marginBottom: 12 }}>
              <div style={{ position: 'relative' }}>
                <input type="text" placeholder="Search by document number..."
                  value={searchDocNumber}
                  onChange={e => setSearchDocNumber(e.target.value)}
                  style={{ ...inputStyle, paddingLeft: 38 }}
                  onFocus={focusInputCyan} onBlur={blurInputDefault} />
                <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: C_TEXT_3, fontSize: 14, pointerEvents: 'none' }}>🔍</span>
              </div>
              <input type="text" placeholder="Search by customer name..."
                value={searchCustomerName}
                onChange={e => setSearchCustomerName(e.target.value)}
                style={inputStyle}
                onFocus={focusInputCyan} onBlur={blurInputDefault} />
              <input type="text" placeholder="Search by P.O. number..."
                value={searchPoNumber}
                onChange={e => setSearchPoNumber(e.target.value)}
                style={inputStyle}
                onFocus={focusInputCyan} onBlur={blurInputDefault} />
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <label style={{ color: C_TEXT_3, fontSize: 12, whiteSpace: 'nowrap' }}>From</label>
                <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                  className="dark-date"
                  style={{ ...inputStyle, flex: 1 }}
                  onFocus={focusInputCyan} onBlur={blurInputDefault} />
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <label style={{ color: C_TEXT_3, fontSize: 12, whiteSpace: 'nowrap' }}>To</label>
                <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                  className="dark-date"
                  style={{ ...inputStyle, flex: 1 }}
                  onFocus={focusInputCyan} onBlur={blurInputDefault} />
              </div>
            </div>

            {/* Admin salesperson filter + payment status filter */}
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16, marginBottom: 12 }}>
              {isAdmin && salespersonNames.length > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <label style={{ color: C_TEXT_2, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>Salesperson:</label>
                  <select value={salespersonFilter}
                    onChange={e => setSalespersonFilter(e.target.value)}
                    className="dark-select"
                    style={{ ...inputStyle, width: 'auto', minWidth: 180, cursor: 'pointer' }}>
                    {salespersonNames.map(name => (
                      <option key={name} value={name}>{name === 'All' ? 'All Salespersons' : name}</option>
                    ))}
                  </select>
                </div>
              )}
              {activeTab === 'invoices' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <label style={{ color: C_TEXT_2, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>Payment:</label>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {/* Credit is its OWN option, not folded into Paid. A credit
                        is money we are holding for the customer — something to
                        refund or apply — and a filter is the only way to list
                        every one of them. Folded into Paid it would disappear
                        among thousands of settled invoices, which is what the
                        old zero-balance rounding did. Separate options also
                        keep the rule simple: each option shows exactly the
                        invoices wearing that one badge. */}
                    {['All', PAYMENT_STATUS.UNPAID, PAYMENT_STATUS.PARTIAL, PAYMENT_STATUS.PAID, PAYMENT_STATUS.CREDIT].map(status => {
                      const active = paymentStatusFilter === status;
                      const styleByStatus = {
                        [PAYMENT_STATUS.PAID]: { bg: `${C_GREEN}26`, border: `${C_GREEN}66`, text: C_GREEN_BRIGHT },
                        [PAYMENT_STATUS.PARTIAL]: { bg: `${C_ORANGE}26`, border: `${C_ORANGE}66`, text: C_ORANGE_BRIGHT },
                        [PAYMENT_STATUS.UNPAID]: { bg: `${C_RED}26`, border: `${C_RED}66`, text: C_RED_BRIGHT },
                        [PAYMENT_STATUS.CREDIT]: { bg: `${C_VIOLET}26`, border: `${C_VIOLET}66`, text: C_VIOLET_BRIGHT },
                        All: { bg: `${C_CYAN}26`, border: `${C_CYAN}66`, text: C_CYAN_BRIGHT },
                      }[status];
                      return (
                        <button key={status}
                          onClick={() => setPaymentStatusFilter(status)}
                          style={{
                            padding: '6px 12px', borderRadius: 8,
                            fontSize: 11, fontWeight: 700,
                            cursor: 'pointer',
                            background: active ? styleByStatus.bg : C_BG_NESTED,
                            border: `1px solid ${active ? styleByStatus.border : C_BORDER}`,
                            color: active ? styleByStatus.text : C_TEXT_2,
                            transition: 'background 0.2s, border-color 0.2s, color 0.2s',
                          }}>
                          {status}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {activeTab === 'invoices' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <label style={{ color: C_TEXT_2, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>Inventory:</label>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {[
                      { key: 'all', label: 'All' },
                      { key: 'pending', label: '🔴 Pending' },
                      { key: 'deducted', label: '🟢 Deducted' },
                    ].map(opt => {
                      const active = inventoryFilter === opt.key;
                      const styleByKey = {
                        deducted: { bg: `${C_GREEN}26`, border: `${C_GREEN}66`, text: C_GREEN_BRIGHT },
                        pending: { bg: `${C_RED}26`, border: `${C_RED}66`, text: C_RED_BRIGHT },
                        all: { bg: `${C_CYAN}26`, border: `${C_CYAN}66`, text: C_CYAN_BRIGHT },
                      }[opt.key];
                      return (
                        <button key={opt.key}
                          onClick={() => setInventoryFilter(opt.key)}
                          style={{
                            padding: '6px 12px', borderRadius: 8,
                            fontSize: 11, fontWeight: 700,
                            cursor: 'pointer',
                            background: active ? styleByKey.bg : C_BG_NESTED,
                            border: `1px solid ${active ? styleByKey.border : C_BORDER}`,
                            color: active ? styleByKey.text : C_TEXT_2,
                            transition: 'background 0.2s, border-color 0.2s, color 0.2s',
                          }}>
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
              <p style={{ color: C_TEXT_2, fontSize: 13, margin: 0 }}>
                Showing <span style={{ color: accentColor, fontWeight: 700 }}>{documents.length}</span> of{' '}
                <span style={{ color: C_TEXT, fontWeight: 700 }}>{roleFilteredDocuments.length}</span> {activeTab}
                {isSalesperson && <span style={{ color: C_TEXT_3, marginLeft: 6 }}>(your documents only)</span>}
                {unknownLeftOut.length > 0 && (
                  <span style={{ color: C_ORANGE_BRIGHT, fontWeight: 600, marginLeft: 6 }}>
                    · {unknownLeftOut.length} not shown: payment status {unknownLeftOutFailed > 0 ? 'unavailable' : 'still loading'}
                  </span>
                )}
              </p>
              {hasFilters && (
                <button onClick={clearFilters}
                  style={{ background: 'transparent', border: 'none', color: C_CYAN_BRIGHT, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                  ✕ Clear Filters
                </button>
              )}
            </div>
          </div>

          {/* Payment records could not be read for some invoices. Those rows
              show STATUS UNAVAILABLE — neither paid nor unpaid — and never the
              status stored on the invoice. */}
          {payErrorCount > 0 && !loading && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
              background: `${C_ORANGE}14`, border: `1px solid ${C_ORANGE}66`, color: C_ORANGE_BRIGHT,
              borderRadius: 12, padding: '10px 16px', marginBottom: 12, fontSize: 13, fontWeight: 600,
            }}>
              <span>
                ⚠️ Payment status could not be loaded for {payErrorCount} invoice{payErrorCount === 1 ? '' : 's'}.
                They show STATUS UNAVAILABLE until it loads — not paid, not unpaid.
              </span>
              <button onClick={retryPayments}
                style={{ ...darkButton, padding: '6px 14px', fontSize: 12, color: C_ORANGE_BRIGHT, borderColor: `${C_ORANGE}66` }}>
                Retry
              </button>
            </div>
          )}

          {/* Documents list */}
          {loading || roleLoading ? (
            <div style={{
              background: C_BG_CARD,
              border: `1px dashed ${C_BORDER}`,
              borderRadius: 16, padding: '60px 20px', textAlign: 'center',
            }}>
              <p style={{ color: C_TEXT_3, fontSize: 16, margin: 0 }}>Loading documents…</p>
            </div>
          ) : searching ? (
            <div style={{
              background: C_BG_CARD,
              border: `1px dashed ${C_BORDER}`,
              borderRadius: 16, padding: '60px 20px', textAlign: 'center',
            }}>
              <p style={{ color: C_TEXT_3, fontSize: 16, margin: 0 }}>Searching…</p>
            </div>
          ) : documents.length === 0 ? (
            <div style={{
              background: C_BG_CARD,
              border: `1px dashed ${C_BORDER}`,
              borderRadius: 16, padding: '60px 20px', textAlign: 'center',
            }}>
              <p style={{ color: C_TEXT_3, fontSize: 16, marginBottom: 8 }}>
                {hasFilters ? 'No documents match your filters.' : `No ${activeTab} found`}
              </p>
              {!hasFilters && (
                <p style={{ color: C_TEXT_3, fontSize: 13, margin: 0 }}>
                  Create your first {activeTab === 'quotes' ? 'quote' : 'invoice'} to get started
                </p>
              )}
            </div>
          ) : (
            <div>
              {documents.map(docItem => {
                const isOtherSalesperson = isAdmin && docItem.salesperson !== roleName;
                const docTypeSingular = activeTab === 'quotes' ? 'quote' : 'invoice';
                return (
                  <div
                    key={docItem.id}
                    onClick={() => navigate(`/edit-document/${docTypeSingular}/${docItem.id}`)}
                    style={{
                      background: C_BG_CARD,
                      backdropFilter: 'none',
                      border: `1px solid ${C_BORDER}`,
                      borderRadius: 14, padding: '16px 20px',
                      marginBottom: 10, cursor: 'pointer',
                      transition: 'transform 0.3s, border-color 0.3s, box-shadow 0.3s, background 0.3s',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      gap: 16, flexWrap: 'wrap',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.background = '#E6F1FB';
                      e.currentTarget.style.borderColor = '#0077B6';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.background = C_BG_CARD;
                      e.currentTarget.style.borderColor = C_BORDER;
                    }}
                  >
                    {/* Left: Doc # + customer */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16, flex: 1, minWidth: 240, flexWrap: 'wrap' }}>
                      <span style={{
                        fontFamily: 'monospace',
                        color: accentColor,
                        fontSize: 14, fontWeight: 700,
                        background: accentBg,
                        padding: '6px 12px', borderRadius: 8,
                        border: `1px solid ${accentBorder}`,
                        flexShrink: 0,
                      }}>
                        #{docItem.docNumber}
                      </span>
                      <div style={{ minWidth: 0 }}>
                        <p style={{ color: C_TEXT, fontWeight: 700, fontSize: 15, margin: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
                          {isOtherSalesperson && <span title="Other salesperson's document">🔒</span>}
                          {docItem.customerName}
                        </p>
                        <p style={{ color: C_TEXT_3, fontSize: 12, margin: '2px 0 0 0' }}>
                          {formatDate(docItem.createdAt)} · {docItem.salesperson}
                          {docItem.poNumber && (
                            <> · <span style={{ fontFamily: 'monospace', color: C_TEXT_2 }}>P.O. {docItem.poNumber}</span></>
                          )}
                        </p>
                      </div>
                    </div>

                    {/* Middle: badges */}
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                      {docItem.priceLevel && (() => {
                        const pl = docItem.priceLevel;
                        const c = pl === 'A' ? { bg: `${C_CYAN}26`, text: C_CYAN_BRIGHT }
                          : pl === 'B' ? { bg: `${C_GREEN}26`, text: C_GREEN_BRIGHT }
                          : { bg: `${C_ORANGE}26`, text: C_ORANGE_BRIGHT };
                        return (
                          <span style={{
                            background: c.bg, color: c.text,
                            padding: '4px 10px', borderRadius: 6,
                            fontSize: 11, fontWeight: 700, letterSpacing: 0.5,
                          }}>
                            {pl}
                          </span>
                        );
                      })()}

                      {/* Pick status — READ ONLY. Set by the office in
                          ViewDocument and by the shop on its own screen; this
                          list only reports it. Nothing renders for an invoice
                          that was never sent, which is most of them. */}
                      {activeTab === 'invoices' && (() => {
                        const badge = pickBadge(docItem);
                        if (!badge) return null;
                        return (
                          <span
                            title={badge.key === 'Ready'
                              ? `Picked by ${docItem.pickStatusBy || '—'}`
                              : `Sent to the shop by ${docItem.pickRequestedBy || '—'}`}
                            style={{
                              background: badge.bg, color: badge.fg,
                              padding: '4px 12px', borderRadius: 8,
                              fontSize: 11, fontWeight: 700, letterSpacing: 0.5,
                            }}
                          >
                            {badge.icon} {badge.label.toUpperCase()}
                          </span>
                        );
                      })()}

                      {/* Back order — READ ONLY, and a SEPARATE FACT from the
                          pick status beside it. "Ready" means the shop has
                          gathered the order; this means the customer has not
                          yet carried all of it away. An order can be Ready with
                          three lines still outstanding, or Not pulled with
                          nothing outstanding, so the two must not be merged
                          into one badge or one colour. Hence a different shape:
                          an outlined chip with a count, not a filled pill. */}
                      {activeTab === 'invoices' && (() => {
                        if (!hasBackOrder(docItem)) return null;
                        const left = backOrderCount(docItem);
                        return (
                          <span
                            title={`${left} of ${collectableCount(docItem)} lines not yet collected`}
                            style={{
                              background: 'transparent', color: '#8A3D00',
                              border: '1.5px solid #C77700',
                              padding: '3px 10px', borderRadius: 8,
                              fontSize: 11, fontWeight: 800, letterSpacing: 0.5,
                            }}
                          >
                            ⬚ BACK ORDER {left}
                          </span>
                        );
                      })()}

                      {/* Payment — COMPUTED from this invoice's payment records
                          through getPaymentStatus, the same result the invoice
                          screen's badge shows. Not the isPaid / isPartialPaid /
                          totalPaid stored on the invoice, which go stale when an
                          invoice is edited after it was paid. */}
                      {activeTab === 'invoices' && (() => {
                        const { state, result } = listPaymentStatus(docItem);
                        const chip = { padding: '4px 12px', borderRadius: 8, fontSize: 11, fontWeight: 700, letterSpacing: 0.5 };
                        if (state === 'error') return (
                          <span title="The payment records for this invoice could not be loaded, so whether it is paid is not known. Use Retry above the list."
                            style={{ ...chip, background: 'transparent', color: C_TEXT_2, border: `1px dashed ${C_TEXT_2}` }}>
                            ⚠️ STATUS UNAVAILABLE
                          </span>
                        );
                        if (state === 'loading') return (
                          <span title="Loading payment records" style={{ ...chip, background: neutralBg, color: C_TEXT_3, fontWeight: 600 }}>
                            CHECKING…
                          </span>
                        );
                        if (result.status === PAYMENT_STATUS.CREDIT) return (
                          <span title="Overpaid: the customer has paid more than this invoice's total" style={{ ...chip, background: `${C_VIOLET}26`, color: C_VIOLET_BRIGHT }}>
                            💙 CREDIT ${result.creditAmount.toFixed(2)}
                          </span>
                        );
                        if (result.status === PAYMENT_STATUS.PAID) return (
                          <span style={{ ...chip, background: `${C_GREEN}26`, color: C_GREEN_BRIGHT }}>
                            ✓ PAID
                          </span>
                        );
                        if (result.status === PAYMENT_STATUS.PARTIAL) return (
                          <span style={{ ...chip, background: `${C_ORANGE}26`, color: C_ORANGE_BRIGHT }}>
                            PARTIAL · ${result.balanceDue.toFixed(2)} DUE
                          </span>
                        );
                        return (
                          <span style={{ ...chip, background: `${C_RED}26`, color: C_RED_BRIGHT }}>
                            UNPAID
                          </span>
                        );
                      })()}

                      {activeTab === 'invoices' && (
                        resolveInventoryStatus(docItem) === 'deducted' ? (
                          <span style={{ background: '#E8F5F0', color: '#0F6E56', padding: '4px 10px', borderRadius: 8, fontSize: 11, fontWeight: 700, letterSpacing: 0.5 }}>
                            🟢 DEDUCTED
                          </span>
                        ) : (
                          <span style={{ background: '#FDEAEA', color: '#A32D2D', padding: '4px 10px', borderRadius: 8, fontSize: 11, fontWeight: 700, letterSpacing: 0.5 }}>
                            🔴 PENDING
                          </span>
                        )
                      )}

                      {activeTab === 'invoices' && docItem.status && (
                        <span style={{
                          background: neutralBg,
                          color: C_TEXT_2,
                          padding: '4px 10px', borderRadius: 8,
                          fontSize: 11, fontWeight: 600,
                        }}>
                          {docItem.status}
                        </span>
                      )}

                      {activeTab === 'quotes' && docItem.convertedToInvoice && (
                        <span style={{ background: `${C_GREEN}26`, color: C_GREEN_BRIGHT, padding: '4px 10px', borderRadius: 8, fontSize: 11, fontWeight: 700, letterSpacing: 0.5 }}>
                          → {docItem.convertedToInvoice}
                        </span>
                      )}

                      {activeTab === 'invoices' && docItem.convertedFromQuote && (
                        <span style={{ background: `${C_VIOLET}1A`, color: C_VIOLET_BRIGHT, padding: '4px 10px', borderRadius: 8, fontSize: 11, fontWeight: 600 }}>
                          From {docItem.convertedFromQuote}
                        </span>
                      )}

                      {docItem.clonedFrom && (
                        <span style={{ background: neutralBg, color: C_TEXT_3, padding: '4px 10px', borderRadius: 8, fontSize: 11, fontWeight: 600 }}>
                          📋 from {docItem.clonedFrom}
                        </span>
                      )}
                    </div>

                    {/* Right: total + actions */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                      <span style={{
                        fontSize: 20, fontWeight: 800,
                        color: '#0077B6',
                        whiteSpace: 'nowrap',
                      }}>
                        ${parseFloat(docItem.grandTotal || 0).toFixed(2)}
                      </span>
                      <div style={{ display: 'flex', gap: 6 }} onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => navigate(`/edit-document/${docTypeSingular}/${docItem.id}`)}
                          style={{ ...cyanGlassButton, padding: '6px 12px', fontSize: 11 }}
                          onMouseEnter={e => e.currentTarget.style.background = `${C_CYAN}2E`}
                          onMouseLeave={e => e.currentTarget.style.background = `${C_CYAN}1A`}>
                          ✏️ Open
                        </button>
                        <button onClick={() => navigate(`/view-document/${docTypeSingular}/${docItem.id}`)}
                          title="Quick Preview"
                          style={{ ...violetGlassButton, padding: '6px 10px', fontSize: 11 }}
                          onMouseEnter={e => e.currentTarget.style.background = `${C_VIOLET}2E`}
                          onMouseLeave={e => e.currentTarget.style.background = `${C_VIOLET}1A`}>
                          👁️
                        </button>
                        {isAdmin && (
                          <button onClick={() => initiateDelete(docItem)}
                            disabled={deleting === docItem.id}
                            style={{ ...redGlassButton, opacity: deleting === docItem.id ? 0.5 : 1 }}
                            onMouseEnter={e => { if (deleting !== docItem.id) e.currentTarget.style.background = `${C_RED}2E`; }}
                            onMouseLeave={e => { e.currentTarget.style.background = `${C_RED}1A`; }}>
                            {deleting === docItem.id ? '…' : '🗑️'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

              {hasMore && (
                <button onClick={loadMoreDocuments} disabled={loadingMore}
                  style={{
                    ...darkButton, width: '100%', padding: 14, marginTop: 8,
                    opacity: loadingMore ? 0.5 : 1,
                  }}
                  onMouseEnter={e => { if (!loadingMore) e.currentTarget.style.background = neutralHover; }}
                  onMouseLeave={e => { e.currentTarget.style.background = neutralBg; }}>
                  {loadingMore ? 'Loading…' : '↓ Load More Documents'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Delete confirmation modal */}
      {showDeleteModal && docToDelete && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 50,
          background: 'rgba(3, 4, 94, 0.35)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 20,
        }}
          onClick={() => { if (!deleting) { setShowDeleteModal(false); setDeletePassword(''); setDeleteError(''); } }}
        >
          <div
            style={{
              background: C_BG_CARD_SOLID,
              border: `1px solid ${C_RED}4D`,
              borderRadius: 20, padding: 28,
              maxWidth: 460, width: '100%',
              boxShadow: '0 20px 50px rgba(3,4,94,0.18)',
              position: 'relative',
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ color: C_RED_BRIGHT, fontSize: 20, fontWeight: 800, margin: 0 }}>⚠️ Delete Document</h3>
            <p style={{ color: C_TEXT_2, fontSize: 14, lineHeight: 1.5, margin: '12px 0 16px 0' }}>
              You are about to permanently delete{' '}
              <strong style={{ color: C_TEXT }}>
                {docToDelete.docType === 'quote' ? 'Quote' : 'Invoice'} #{docToDelete.docNumber}
              </strong>{' '}
              for <strong style={{ color: C_TEXT }}>{docToDelete.customerName}</strong>. This cannot be undone.
            </p>
            <label style={labelStyle}>Enter your password to confirm</label>
            <input
              type="password"
              value={deletePassword}
              onChange={e => setDeletePassword(e.target.value)}
              placeholder="Your login password"
              autoFocus
              onKeyDown={e => { if (e.key === 'Enter' && deletePassword) confirmDelete(); }}
              style={inputStyle}
              onFocus={e => {
                e.target.style.borderColor = `${C_RED}80`;
                e.target.style.boxShadow = `0 0 0 3px ${C_RED}1A`;
              }}
              onBlur={blurInputDefault}
            />
            {deleteError && (
              <p style={{ color: C_RED_BRIGHT, fontSize: 13, marginTop: 8, marginBottom: 0 }}>{deleteError}</p>
            )}
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button
                onClick={confirmDelete}
                disabled={!!deleting || !deletePassword}
                style={{
                  flex: 1,
                  background: `${C_RED}26`,
                  border: `1px solid ${C_RED}66`,
                  color: C_RED_BRIGHT,
                  padding: '12px 16px', borderRadius: 12,
                  fontSize: 14, fontWeight: 700,
                  cursor: (deleting || !deletePassword) ? 'not-allowed' : 'pointer',
                  opacity: (deleting || !deletePassword) ? 0.5 : 1,
                  transition: 'background 0.3s',
                }}
                onMouseEnter={e => { if (!deleting && deletePassword) e.currentTarget.style.background = `${C_RED}40`; }}
                onMouseLeave={e => { e.currentTarget.style.background = `${C_RED}26`; }}
              >
                {deleting ? 'Deleting…' : 'Delete Permanently'}
              </button>
              <button
                onClick={() => { setShowDeleteModal(false); setDeletePassword(''); setDeleteError(''); }}
                style={{ ...darkButton, flex: 1, padding: '12px 16px', fontSize: 14 }}
                onMouseEnter={e => e.currentTarget.style.background = neutralHover}
                onMouseLeave={e => e.currentTarget.style.background = neutralBg}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Documents;
